import React from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAuthenticatedUser } from "api/auth.api";
import { getActiveCompany, getMyCompanies } from "api/company.api";
import { QUERY_KEYS } from "config/queryKeys";
import { ROUTES } from "config/routes";

const MARKETING_SITE_URL = "https://www.auditguardai.com";

// ---------------------------------------------------------------------------
// Loading spinner
// ---------------------------------------------------------------------------

const AuthLoadingSpinner = () => (
  <div
    className="flex items-center justify-center min-h-screen bg-background"
    aria-label="Verifying authentication"
    role="status"
  >
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      <p className="text-sm text-muted-foreground">Verifying session...</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const hasActiveCompanyFromAuth = (authData) => {
  const user = authData?.user ?? authData;

  return Boolean(
    authData?.activeCompany ||
    authData?.company ||
    authData?.companyId ||
    user?.activeCompany ||
    user?.company ||
    user?.companyId,
  );
};

const hasActiveCompanyContext = (activeCompanyData) => {
  return Boolean(
    activeCompanyData?.hasActiveCompany ||
    activeCompanyData?.company ||
    activeCompanyData?.context?.companyId ||
    activeCompanyData?.data?.hasActiveCompany ||
    activeCompanyData?.data?.company ||
    activeCompanyData?.data?.context?.companyId,
  );
};

const getCompaniesArray = (data) => {
  const companies =
    data?.data?.companies || data?.companies || data?.data || [];
  return Array.isArray(companies) ? companies : [];
};

const RedirectToMarketingSite = () => {
  React.useEffect(() => {
    window.location.replace(MARKETING_SITE_URL);
  }, []);

  return <AuthLoadingSpinner />;
};

// ---------------------------------------------------------------------------
// ProtectedRoute
// ---------------------------------------------------------------------------

const ProtectedRoute = ({ children, requireCompany = true }) => {
  const {
    data: authData,
    isLoading: isAuthLoading,
    isError: isAuthError,
  } = useQuery({
    queryKey: QUERY_KEYS.auth.me(),
    queryFn: getAuthenticatedUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const shouldCheckActiveCompany =
    requireCompany && Boolean(authData) && !hasActiveCompanyFromAuth(authData);

  const {
    data: activeCompanyData,
    isLoading: isActiveCompanyLoading,
    isError: isActiveCompanyError,
  } = useQuery({
    queryKey: QUERY_KEYS.companies.active(),
    queryFn: getActiveCompany,
    retry: false,
    enabled: shouldCheckActiveCompany,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const shouldCheckCompanies =
    requireCompany &&
    Boolean(authData) &&
    !hasActiveCompanyFromAuth(authData) &&
    !hasActiveCompanyContext(activeCompanyData) &&
    !isActiveCompanyLoading;

  const {
    data: companiesData,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useQuery({
    queryKey: QUERY_KEYS.companies.mine(),
    queryFn: getMyCompanies,
    retry: false,
    enabled: shouldCheckCompanies,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  if (isAuthLoading || isActiveCompanyLoading || isCompaniesLoading) {
    return <AuthLoadingSpinner />;
  }

  if (isAuthError || !authData) {
    return <RedirectToMarketingSite />;
  }

  if (!requireCompany) {
    return children;
  }

  if (hasActiveCompanyFromAuth(authData)) {
    return children;
  }

  if (!isActiveCompanyError && hasActiveCompanyContext(activeCompanyData)) {
    return children;
  }

  if (isCompaniesError) {
    return <Navigate to={ROUTES.COMPANY_ONBOARDING} replace />;
  }

  const companies = getCompaniesArray(companiesData);

  if (companies.length === 0) {
    return <Navigate to={ROUTES.COMPANY_ONBOARDING} replace />;
  }

  return <Navigate to={ROUTES.COMPANY_SELECT} replace />;
};

export default ProtectedRoute;
